import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/layout/AppShell";
import FeatureGate from "@/components/FeatureGate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  BookOpen, Upload, ChevronLeft, ChevronRight, Loader2, FileText,
  Image, FileSpreadsheet, Download, Printer, Trash2, Mail, StickyNote, X, Save, Edit2, Copy, UserCog
} from "lucide-react";
import LessonPlanTemplates, { PlanViewer, THEMES, openPrintWindow } from "@/components/lesson-planning/LessonPlanTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// Build a clean printable HTML document from an uploaded Excel sheet's 2D row data
function buildExcelPrintHtml({ rows, title, sheetName, isMultiSheet }) {
  const headerColor = "#2F6DB5";
  const tableHtml = rows.map((row, i) => {
    const isTitleRow = i === 0;
    const isHeaderRow = i === 1;
    const bgStyle = isTitleRow || isHeaderRow
      ? `background:${headerColor};color:white;font-weight:bold;`
      : i % 2 === 0 ? "background:white;" : "background:#eff6ff;";
    const cells = row.map(cell => {
      const escaped = (cell || "").toString()
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      return `<td style="border:1px solid #ccc;padding:6px 8px;font-size:9px;vertical-align:top;word-wrap:break-word;overflow-wrap:break-word">${escaped}</td>`;
    }).join("");
    return `<tr style="${bgStyle}">${cells}</tr>`;
  }).join("");

  const safeTitle = (title || "Lesson Plan").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<!DOCTYPE html><html><head><title>${safeTitle}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { background: white; color: #111; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; padding: 0.5in; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    td { border: 1px solid #ccc; padding: 6px 8px; vertical-align: top; font-size: 9px; word-wrap: break-word; overflow-wrap: break-word; }
    tr { page-break-inside: avoid; }
    @page { size: 11in 8.5in landscape; margin: 0.3in; }
    @media print { body { padding: 0.3in 0 0 0; } td { font-size: 8px !important; padding: 4px 6px !important; } }
  </style></head><body>
  <div style="border-bottom:2px solid ${headerColor};padding-bottom:8px;margin-bottom:10px">
    <div style="font-size:16px;font-weight:bold;color:${headerColor}">${safeTitle}</div>
    ${isMultiSheet && sheetName ? `<div style="font-size:11px;color:#555;margin-top:2px">Sheet: ${sheetName}</div>` : ""}
  </div>
  <table><tbody>${tableHtml}</tbody></table>
  </body></html>`;
}

// Inline file viewer for lesson plans
const LessonFileViewer = ({ plan, token }) => {
  const fileUrl = `${API}/lesson-plans/${plan.id}/file?token=${encodeURIComponent(token)}`;

  if (plan.file_type === 'pdf') {
    return (
      <div className="mt-3">
        <iframe
          src={fileUrl}
          className="w-full rounded-lg border border-gray-200"
          style={{ height: "500px" }}
          title={plan.title}
        />
      </div>
    );
  }

  if (plan.file_type === 'xls' || plan.file_type === 'xlsx') {
    return <LessonExcelView plan={plan} fileUrl={fileUrl} token={token} />;
  }

  if (['png','jpg','jpeg'].includes(plan.file_type)) {
    return (
      <div className="mt-3 flex justify-center">
        <img src={fileUrl} alt={plan.title} className="max-w-full rounded-lg border border-gray-200" style={{ maxHeight: "500px" }} />
      </div>
    );
  }

  return (
    <div className="mt-3 flex justify-center">
      <a href={fileUrl} download={plan.filename} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
        <Download className="w-4 h-4" />
        Download {plan.filename}
      </a>
    </div>
  );
};

// Excel table viewer (with click-to-edit) for lesson plans
const LessonExcelView = ({ plan, fileUrl, token }) => {
  const [sheets, setSheets] = useState([]);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const [originalSheets, setOriginalSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lesson-plans/${plan.id}/excel-data?token=${encodeURIComponent(token)}`)
      .then(res => {
        // New shape: { sheets: [...] }. Legacy shape: { rows: [...] }
        let parsed = res.data.sheets;
        if (!parsed && Array.isArray(res.data.rows)) {
          parsed = [{ name: "Sheet1", rows: res.data.rows, merges: [] }];
        }
        const safe = (parsed || []).map(s => ({
          name: s.name || "Sheet",
          rows: (s.rows || []).map(r => [...r]),
          merges: s.merges || [],
        }));
        setSheets(safe);
        setOriginalSheets(safe.map(s => ({ ...s, rows: s.rows.map(r => [...r]) })));
        setActiveSheetIdx(0);
      })
      .catch(() => setSheets([]))
      .finally(() => setLoading(false));
  }, [plan.id, token]);

  const activeSheet = sheets[activeSheetIdx];
  const rows = activeSheet?.rows || [];
  const merges = activeSheet?.merges || [];

  // Build a set of "covered" cells (those hidden under a merge) and "anchor" info
  const mergeAnchors = {}; // "r,c" -> { rowSpan, colSpan }
  const mergeCovered = new Set(); // "r,c" of cells to skip rendering
  merges.forEach(m => {
    mergeAnchors[`${m.minRow},${m.minCol}`] = {
      rowSpan: m.maxRow - m.minRow + 1,
      colSpan: m.maxCol - m.minCol + 1,
    };
    for (let r = m.minRow; r <= m.maxRow; r++) {
      for (let c = m.minCol; c <= m.maxCol; c++) {
        if (r === m.minRow && c === m.minCol) continue;
        mergeCovered.add(`${r},${c}`);
      }
    }
  });

  const updateCell = (i, j, v) => {
    setSheets(prev => prev.map((s, si) => {
      if (si !== activeSheetIdx) return s;
      const newRows = s.rows.map((row, ri) =>
        ri === i ? row.map((c, ci) => ci === j ? v : c) : row
      );
      return { ...s, rows: newRows };
    }));
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const orig = originalSheets[activeSheetIdx]?.rows || [];
      const edited = rows.map((row, i) =>
        row.map((cell, j) => {
          const origCell = orig[i]?.[j] ?? "";
          return cell !== origCell ? cell : null;
        })
      );
      await axios.patch(
        `${API}/lesson-plans/${plan.id}/excel-data`,
        { edited_rows: edited, sheet_name: activeSheet?.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update originals for this sheet
      setOriginalSheets(prev => prev.map((s, si) =>
        si === activeSheetIdx
          ? { ...s, rows: rows.map(r => [...r]) }
          : s
      ));
      setEditMode(false);
      toast.success(`Edits saved to "${activeSheet?.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save edits");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdits = () => {
    setSheets(prev => prev.map((s, si) =>
      si === activeSheetIdx
        ? { ...s, rows: (originalSheets[si]?.rows || []).map(r => [...r]) }
        : s
    ));
    setEditMode(false);
  };

  const handlePrint = () => {
    const html = buildExcelPrintHtml({
      rows,
      title: plan.title || plan.filename || "Lesson Plan",
      sheetName: activeSheet?.name,
      isMultiSheet: sheets.length > 1,
    });
    openPrintWindow(html);
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>;

  if (sheets.length === 0 || rows.length === 0) {
    return (
      <div className="mt-3 flex justify-center">
        <a href={fileUrl} download={plan.filename} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90">
          <Download className="w-4 h-4" /> Download Excel
        </a>
      </div>
    );
  }

  const styledDownloadUrl = `${API}/lesson-plans/${plan.id}/download-styled?token=${encodeURIComponent(token)}`;

  return (
    <div className="mt-3 space-y-2 lesson-plan-printable">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .lesson-plan-printable, .lesson-plan-printable * { visibility: visible !important; }
          .lesson-plan-printable { position: absolute; top: 0; left: 0; width: 100%; padding: 12px; }
          .lesson-plan-table { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .lp-title-row td, .lp-header-row td {
            background: #2F6DB5 !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .lesson-plan-table tr { page-break-inside: avoid; }
        }
      `}</style>
      {/* Sheet tabs (only show if multi-sheet) */}
      {sheets.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-1 print:hidden" data-testid="lesson-sheet-tabs">
          {sheets.map((s, idx) => (
            <button
              key={idx}
              onClick={() => { setActiveSheetIdx(idx); setEditMode(false); }}
              disabled={editMode}
              data-testid={`lesson-sheet-tab-${idx}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg border border-b-0 transition ${
                idx === activeSheetIdx
                  ? "bg-brand-primary text-white border-brand-primary"
                  : "bg-blue-50 text-brand-primary border-blue-200 hover:bg-blue-100"
              } disabled:opacity-60`}
            >
              <span className="opacity-70 mr-1">Week {idx + 1}</span>
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2 print:hidden">
        {editMode ? (
          <>
            <button
              onClick={cancelEdits}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              data-testid="lesson-excel-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveEdits}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 disabled:opacity-60"
              data-testid="lesson-excel-save"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? "Saving..." : "Save Edits"}
            </button>
          </>
        ) : (
          <button
            onClick={() => setEditMode(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-secondary text-white rounded-lg hover:bg-brand-secondary/90"
            data-testid="lesson-excel-edit"
          >
            <Edit2 className="w-3 h-3" /> Edit Cells
          </button>
        )}
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-600 text-white rounded-lg hover:bg-slate-700"
          data-testid="lesson-excel-print"
        >
          🖨️ Print
        </button>
        <a
          href={styledDownloadUrl}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
          data-testid="lesson-excel-download-styled"
        >
          <Download className="w-3 h-3" /> Download {sheets.length > 1 ? "All Weeks" : "Excel"}
        </a>
      </div>

      <div className="overflow-x-auto rounded-lg border border-blue-200 shadow-sm">
        <table className="w-full text-sm lesson-plan-table">
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className={
                  i === 0 ? "lp-title-row" :
                  i === 1 ? "lp-header-row" :
                  i % 2 === 0 ? "bg-white" : "bg-blue-50/60"
                }
              >                {row.map((cell, j) => {
                  const key = `${i},${j}`;
                  if (mergeCovered.has(key)) return null;
                  const anchor = mergeAnchors[key];
                  const isTitleRow = i === 0;
                  const isHeaderRow = i === 1;
                  const tintedCellClass =
                    isTitleRow || isHeaderRow
                      ? "px-3 py-3 bg-brand-primary text-white font-bold text-center border-b border-brand-primary"
                      : "px-3 py-2 border-b border-blue-100 whitespace-pre-wrap align-top text-ink-main";
                  return (
                    <td
                      key={j}
                      rowSpan={anchor?.rowSpan}
                      colSpan={anchor?.colSpan}
                      className={tintedCellClass}
                    >
                      {editMode && !isTitleRow && !isHeaderRow ? (
                        <textarea
                          value={cell}
                          onChange={(e) => updateCell(i, j, e.target.value)}
                          rows={1}
                          className="w-full min-w-[80px] bg-white border border-brand-secondary rounded px-1.5 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          data-testid={`lesson-cell-${i}-${j}`}
                        />
                      ) : cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function LessonPlanningPage() {
  const { user, token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewingMonth, setViewingMonth] = useState(new Date().getMonth() + 1);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [editNotesText, setEditNotesText] = useState("");
  const [emailing, setEmailing] = useState(null);
  const fileInputRef = useRef(null);
  const topCardRef = useRef(null);
  const templateCardRef = useRef(null);
  const startEditRef = useRef(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [orgName, setOrgName] = useState("");
  const currentMonth = new Date().getMonth() + 1;
  const authHeaders = { Authorization: `Bearer ${token}` };
  const isDirector = user?.isOwner || user?.isPrimaryOwner || user?.isAdmin || user?.role === "owner" || user?.role === "co-owner";
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffFilterId, setStaffFilterId] = useState("");
  const [assignToStaffId, setAssignToStaffId] = useState("");
  const [reassigningPlanId, setReassigningPlanId] = useState(null);
  const [uploadedPlan, setUploadedPlan] = useState(null);
  const [savingPostUpload, setSavingPostUpload] = useState(false);

  useEffect(() => {
    if (token) fetchPlans();
  }, [token, viewingMonth, staffFilterId]);

  // When the upload modal opens, default the Month dropdown to whatever month
  // the user is currently viewing — not the calendar's current month.
  useEffect(() => {
    if (uploadModalOpen) {
      setSelectedMonth(viewingMonth);
    }
  }, [uploadModalOpen, viewingMonth]);

  useEffect(() => {
    if (token && isDirector) {
      axios.get(`${API}/users`, { headers: authHeaders })
        .then(r => setStaffUsers(r.data || []))
        .catch(() => {});
    }
    if (token) {
      axios.get(`${API}/organizations/contact-info`, { headers: authHeaders })
        .then(r => setOrgName(r.data?.name || ""))
        .catch(() => {});
    }
  }, [token, isDirector]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ month: viewingMonth });
      if (staffFilterId) params.set("staff_id", staffFilterId);
      const res = await axios.get(`${API}/lesson-plans?${params}`, { headers: authHeaders });
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
    if (!file) return;

    // Auto-derive title from filename if user didn't type one
    // (strip extension, replace underscores/dashes with spaces)
    let effectiveTitle = title.trim();
    if (!effectiveTitle) {
      effectiveTitle = (file.name || "Lesson Plan")
        .replace(/\.[^/.]+$/, "")
        .replace(/[_-]+/g, " ")
        .trim() || "Lesson Plan";
      setTitle(effectiveTitle);
    }

    setUploadError("");
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', selectedMonth.toString());
    formData.append('title', effectiveTitle);
    formData.append('notes', notes);
    if (assignToStaffId && isDirector) {
      formData.append('staff_id', assignToStaffId);
    }

    try {
      const resp = await axios.post(`${API}/lesson-plans/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHeaders }
      });
      toast.success("Lesson plan uploaded!");
      // Don't auto-close — show success state so user can review/edit before closing
      setUploadedPlan(resp.data?.plan || null);
      setUploadError("");
      if (selectedMonth === viewingMonth) fetchPlans();
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const [deleteConfirmPlan, setDeleteConfirmPlan] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = (plan) => {
    // Open in-app confirm dialog (avoids window.confirm being suppressed by browser)
    setDeleteConfirmPlan(plan);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmPlan?.id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/lesson-plans/${deleteConfirmPlan.id}`, { headers: authHeaders });
      toast.success("Deleted");
      setDeleteConfirmPlan(null);
      fetchPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveNotes = async (planId) => {
    try {
      const formData = new FormData();
      formData.append('notes', editNotesText);
      await axios.put(`${API}/lesson-plans/${planId}/notes`, formData, { headers: authHeaders });
      toast.success("Notes saved");
      setEditingNotes(null);
      fetchPlans();
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  const handleEmail = async (planId) => {
    setEmailing(planId);
    try {
      const res = await axios.post(`${API}/lesson-plans/${planId}/email`, {}, { headers: authHeaders });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailing(null);
    }
  };

  const handleLessonPlanPrint = () => {
    window.print();
  };

  const handleSaveTemplateToMonth = async () => {
    if (!viewingTemplate) return;
    try {
      await axios.post(`${API}/lesson-plan-templates`, {
        templateType: viewingTemplate.templateType,
        weekOf: viewingTemplate.weekOf,
        theme: viewingTemplate.theme,
        cells: viewingTemplate.cells,
        rows: viewingTemplate.rows,
        month: viewingMonth,
        year: new Date().getFullYear(),
      }, { headers: authHeaders });
      toast.success(`Plan saved to ${MONTHS[viewingMonth - 1]}`);
      setViewingTemplate(null);
    } catch {
      toast.error("Failed to save plan to month");
    }
  };

  const navigateMonth = (dir) => {
    setViewingMonth(prev => {
      const next = prev + dir;
      if (next < 1) return 12;
      if (next > 12) return 1;
      return next;
    });
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setTitle("");
    setNotes("");
    setAssignToStaffId("");
    setUploadError("");
    setUploadedPlan(null);
  };

  const handleSavePostUpload = async () => {
    if (!uploadedPlan?.id) return;
    setSavingPostUpload(true);
    try {
      const fd = new FormData();
      fd.append('notes', notes);
      if (title.trim()) fd.append('title', title.trim());
      await axios.put(`${API}/lesson-plans/${uploadedPlan.id}/notes`, fd, { headers: authHeaders });
      toast.success("Changes saved");
      fetchPlans();
      closeUploadModal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save changes");
    } finally {
      setSavingPostUpload(false);
    }
  };

  const handleReassign = async (planId, newStaffId) => {
    const target = staffUsers.find(u => u.id === newStaffId);
    const targetName = target?.name || target?.email || "this staff member";
    if (!window.confirm(`Reassign this plan to ${targetName}? They'll be able to see and edit it.`)) {
      return;
    }
    try {
      await axios.post(
        `${API}/lesson-plans/${planId}/reassign`,
        { staff_id: newStaffId },
        { headers: authHeaders }
      );
      toast.success(`Plan reassigned to ${targetName}`);
      setReassigningPlanId(null);
      fetchPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reassign plan");
    }
  };


  const handleCopyToMonth = async (planId, targetMonth) => {
    const monthName = MONTHS[targetMonth - 1];
    if (!window.confirm(`Copy this plan to ${monthName}? The current ${MONTHS[viewingMonth - 1]} plan stays in place.`)) {
      return;
    }
    try {
      await axios.post(
        `${API}/lesson-plans/${planId}/copy-to-month`,
        { target_month: targetMonth },
        { headers: authHeaders }
      );
      toast.success(`Plan copied to ${monthName}. Switch months to view.`);
      fetchPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to copy plan");
    }
  };

  // Group plans by staff
  const plansByStaff = {};
  plans.forEach(p => {
    const key = p.staffName || "Unknown";
    if (!plansByStaff[key]) plansByStaff[key] = [];
    plansByStaff[key].push(p);
  });

  return (
    <AppShell pageTitle="Lesson Planning Hub">
      <FeatureGate feature="staff_compliance_tracker">

      <div data-testid="lesson-planning-page" className="space-y-6">
        {/* Strategy A safety net — when user falls back to browser native print (Ctrl+P)
            without using the Print button, force landscape and unfold scroll containers. */}
        <style>{`
          @media print {
            @page { size: landscape; margin: 0.3in; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; }
            .overflow-x-auto, .overflow-y-auto, .overflow-auto { overflow: visible !important; }
            textarea, input[type="text"] { height: auto !important; min-height: 0 !important; overflow: visible !important; resize: none !important; border: none !important; }
            tr, td { page-break-inside: avoid; }
          }
        `}</style>
        {/* Header */}
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <BookOpen className="w-6 h-6 text-blue-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Lesson Planning Hub</h1>
              <p className="text-sm text-gray-500">Upload and manage lesson plans by month</p>
            </div>
          </div>

          <Dialog open={uploadModalOpen} onOpenChange={(o) => { if (!o) closeUploadModal(); else setUploadModalOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="upload-lesson-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload Lesson Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{uploadedPlan ? "Lesson Plan Uploaded ✓" : "Upload Lesson Plan"}</DialogTitle>
              </DialogHeader>
              {!uploadedPlan ? (
              <div className="space-y-4">
                {/* File picker FIRST so dragging is the natural first action */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, Image, Excel)</label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                      dragging ? 'border-brand-primary bg-blue-50' : 'border-gray-300 hover:border-brand-primary'
                    } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); handleUpload(e); }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx"
                      onChange={handleUpload}
                      disabled={uploading}
                      className="hidden"
                      data-testid="lesson-file-input"
                    />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-brand-primary" />
                      <p className="text-sm text-gray-700 font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400">PDF, Image, Excel, Word (max 10MB)</p>
                    </div>
                  </div>
                </div>

                {uploading && (
                  <div className="flex items-center gap-2 text-sm text-brand-primary" data-testid="lesson-upload-progress">
                    <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                  </div>
                )}

                {uploadError && (
                  <div
                    className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                    data-testid="lesson-upload-error"
                  >
                    <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>{uploadError}</div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-xs text-gray-400 font-normal">(auto-fills from filename if left blank)</span></label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Will use filename if you leave this empty"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="lesson-title-input"
                  />
                </div>

                {isDirector && staffUsers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assign to <span className="text-xs text-gray-400 font-normal">(directors only)</span>
                    </label>
                    <select
                      value={assignToStaffId}
                      onChange={e => setAssignToStaffId(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      data-testid="lesson-assign-to-select"
                    >
                      <option value="">Myself ({user?.name || user?.email})</option>
                      {staffUsers
                        .filter(u => u.id !== user?.id)
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name || u.email}
                            {u.role ? ` — ${u.role}` : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="lesson-month-select"
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Materials needed, special activities, reminders..."
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    rows={2}
                    data-testid="lesson-notes-input"
                  />
                </div>
              </div>
              ) : (
              <div className="space-y-4" data-testid="lesson-upload-success">
                <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white">✓</div>
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-green-900">Uploaded successfully</div>
                    <div className="text-green-800 mt-0.5">
                      <strong>{uploadedPlan.filename}</strong> · {MONTHS[uploadedPlan.month - 1]} {uploadedPlan.year} · {uploadedPlan.staffName}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    data-testid="lesson-postupload-title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Materials needed, special activities, reminders..."
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    rows={3}
                    data-testid="lesson-postupload-notes"
                  />
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    onClick={handleSavePostUpload}
                    disabled={savingPostUpload}
                    className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg"
                    data-testid="lesson-postupload-save"
                  >
                    {savingPostUpload ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes & Close
                  </Button>
                  <Button
                    variant="outline"
                    onClick={closeUploadModal}
                    disabled={savingPostUpload}
                    className="rounded-lg"
                    data-testid="lesson-postupload-close"
                  >
                    Close
                  </Button>
                </div>
              </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete confirmation dialog (in-app, replaces native window.confirm
              which Chrome silently suppresses on some preview domains) */}
          <Dialog
            open={!!deleteConfirmPlan}
            onOpenChange={(o) => { if (!o && !deleting) setDeleteConfirmPlan(null); }}
          >
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Delete this lesson plan?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-gray-700">
                  This will permanently delete:
                </p>
                <div className="p-2 bg-gray-50 border rounded text-gray-800">
                  <div className="font-medium">{deleteConfirmPlan?.title}</div>
                  <div className="text-xs text-gray-500">{deleteConfirmPlan?.filename}</div>
                </div>
                <p className="text-xs text-red-600">This cannot be undone.</p>
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-lg"
                    onClick={() => setDeleteConfirmPlan(null)}
                    disabled={deleting}
                    data-testid="delete-plan-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    onClick={confirmDelete}
                    disabled={deleting}
                    data-testid="delete-plan-confirm"
                  >
                    {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Month Navigation + Plans */}
        <Card ref={topCardRef} className="border-0 shadow-lg rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 print:hidden">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="lesson-prev-month">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-800" data-testid="lesson-viewing-month">
                  {MONTHS[viewingMonth - 1]} Lesson Plans
                </h2>
                {viewingMonth === currentMonth && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Current Month</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="lesson-next-month">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Month pills */}
            <div className="flex flex-wrap gap-1.5 justify-center mb-6 print:hidden">
              {MONTHS.map((month, index) => {
                const monthNum = index + 1;
                const isViewing = viewingMonth === monthNum;
                return (
                  <button
                    key={month}
                    onClick={() => setViewingMonth(monthNum)}
                    data-testid={`lesson-month-pill-${monthNum}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      isViewing ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {month.substring(0, 3)}
                  </button>
                );
              })}
            </div>

            {/* Staff filter — director only */}
            {isDirector && staffUsers.length > 0 && (
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100 print:hidden">
                <label className="text-sm font-medium text-gray-600 whitespace-nowrap">View staff:</label>
                <select
                  value={staffFilterId}
                  onChange={e => setStaffFilterId(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">All Staff</option>
                  {staffUsers.map(s => (
                    <option key={s.id} value={s.id}>{s.name || s.email}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Template plan viewer — shown here when View is clicked from templates */}
            {viewingTemplate && (
              <div className="mb-4">
                <PlanViewer
                  tmpl={viewingTemplate}
                  orgName={orgName}
                  onClose={() => setViewingTemplate(null)}
                  onEdit={(tmpl) => {
                    setViewingTemplate(null);
                    startEditRef.current?.(tmpl);
                    setTimeout(() => templateCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
                  }}
                  onArchive={async (id) => {
                    try {
                      await axios.post(`${API}/lesson-plan-templates/${id}/archive`, {}, { headers: authHeaders });
                      toast.success("Plan archived");
                      setViewingTemplate(null);
                    } catch { toast.error("Failed to archive"); }
                  }}
                  onRestore={async (id) => {
                    try {
                      await axios.post(`${API}/lesson-plan-templates/${id}/restore`, {}, { headers: authHeaders });
                      toast.success("Plan restored");
                      setViewingTemplate(null);
                    } catch { toast.error("Failed to restore"); }
                  }}
                  onPrint={handleLessonPlanPrint}
                  saveToMonthLabel={
                    viewingTemplate.month && viewingTemplate.month !== viewingMonth
                      ? `Save to ${MONTHS[viewingMonth - 1]}`
                      : null
                  }
                  onSaveToMonth={handleSaveTemplateToMonth}
                />
              </div>
            )}

            {/* Plans List */}
            <div className="print:hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-600">No lesson plans for {MONTHS[viewingMonth - 1]}</h3>
                <p className="text-gray-400 text-sm mt-1">Click "Upload Lesson Plan" to add one</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(plansByStaff).map(([staffName, staffPlans]) => (
                  <div key={staffName} className="border border-gray-200 rounded-xl overflow-hidden">
                    {isDirector && (
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <span className="font-semibold text-gray-700 text-sm">{staffName}</span>
                        <span className="text-gray-400 text-xs ml-2">({staffPlans.length} plan{staffPlans.length !== 1 ? 's' : ''})</span>
                      </div>
                    )}
                    {staffPlans.map(plan => (
                      <div key={plan.id} className="border-b last:border-0 border-gray-100">
                        {/* Plan header */}
                        <div
                          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-blue-50/50 transition-colors"
                          onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                          data-testid={`lesson-plan-${plan.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {plan.file_type === 'pdf' ? <FileText className="w-5 h-5 text-red-500" /> :
                             plan.file_type === 'xls' || plan.file_type === 'xlsx' ? <FileSpreadsheet className="w-5 h-5 text-green-600" /> :
                             <Image className="w-5 h-5 text-blue-500" />}
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{plan.title}</p>
                              <p className="text-xs text-gray-400">{plan.filename} &middot; {new Date(plan.uploaded_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {plan.notes && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <StickyNote className="w-3 h-3" /> Notes
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-800 h-7 px-2"
                              onClick={e => { e.stopPropagation(); handleEmail(plan.id); }}
                              disabled={emailing === plan.id}
                              data-testid={`email-plan-${plan.id}`}
                            >
                              {emailing === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            </Button>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-brand-primary hover:text-brand-primary/80 h-7 px-2"
                                  onClick={e => e.stopPropagation()}
                                  data-testid={`copy-plan-${plan.id}`}
                                  title="Copy to another month"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-64 p-3"
                                align="end"
                                onClick={e => e.stopPropagation()}
                              >
                                <div className="text-xs font-semibold text-ink-main mb-2">
                                  Copy this plan to:
                                </div>
                                <div className="grid grid-cols-3 gap-1.5">
                                  {MONTHS.map((mName, mIdx) => {
                                    const mNum = mIdx + 1;
                                    const isCurrent = mNum === viewingMonth;
                                    return (
                                      <button
                                        key={mNum}
                                        disabled={isCurrent}
                                        onClick={() => handleCopyToMonth(plan.id, mNum)}
                                        data-testid={`copy-plan-${plan.id}-to-month-${mNum}`}
                                        className={`px-2 py-1.5 text-xs rounded-md font-semibold transition ${
                                          isCurrent
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-blue-50 text-brand-primary hover:bg-brand-primary hover:text-white"
                                        }`}
                                      >
                                        {mName.substring(0, 3)}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="text-[10px] text-gray-400 mt-2">
                                  Current month is greyed out. Original stays in place.
                                </div>
                              </PopoverContent>
                            </Popover>
                            {isDirector && staffUsers.length > 1 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-indigo-600 hover:text-indigo-800 h-7 px-2"
                                    onClick={e => e.stopPropagation()}
                                    data-testid={`reassign-plan-${plan.id}`}
                                    title="Reassign to another staff member"
                                  >
                                    <UserCog className="w-4 h-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-64 p-3"
                                  align="end"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div className="text-xs font-semibold text-ink-main mb-2">
                                    Reassign this plan to:
                                  </div>
                                  <div className="max-h-60 overflow-y-auto space-y-1">
                                    {staffUsers
                                      .filter(u => u.id !== plan.staffId)
                                      .map(u => (
                                        <button
                                          key={u.id}
                                          onClick={() => handleReassign(plan.id, u.id)}
                                          data-testid={`reassign-plan-${plan.id}-to-${u.id}`}
                                          className="w-full text-left px-2 py-1.5 text-xs rounded-md font-medium text-brand-primary hover:bg-brand-primary hover:text-white"
                                        >
                                          {u.name || u.email}
                                          {u.role ? <span className="text-[10px] opacity-70 ml-1">— {u.role}</span> : null}
                                        </button>
                                      ))}
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-2">
                                    Currently assigned to: <strong>{plan.staffName}</strong>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            <a
                              href={`${API}/lesson-plans/${plan.id}/file?token=${encodeURIComponent(token)}`}
                              download={plan.filename}
                              onClick={e => e.stopPropagation()}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-600 h-7 px-2"
                              onClick={e => { e.stopPropagation(); handleDelete(plan); }}
                              data-testid={`delete-plan-${plan.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {expandedPlan === plan.id && (
                          <div className="px-4 pb-4 bg-gray-50/50">
                            {/* Notes section */}
                            {editingNotes === plan.id ? (
                              <div className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-yellow-800 flex items-center gap-1"><StickyNote className="w-4 h-4" /> Notes</span>
                                  <div className="flex gap-1">
                                    <Button size="sm" className="h-7 text-xs bg-yellow-600 hover:bg-yellow-700 text-white" onClick={() => handleSaveNotes(plan.id)}>Save</Button>
                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingNotes(null)}><X className="w-3 h-3" /></Button>
                                  </div>
                                </div>
                                <textarea
                                  value={editNotesText}
                                  onChange={e => setEditNotesText(e.target.value)}
                                  className="w-full border rounded px-3 py-2 text-sm resize-none"
                                  rows={3}
                                  placeholder="Things needed, reminders..."
                                  data-testid="edit-notes-textarea"
                                />
                              </div>
                            ) : (
                              <div className="mb-3">
                                {plan.notes ? (
                                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-yellow-800 flex items-center gap-1"><StickyNote className="w-4 h-4" /> Notes</span>
                                      <button
                                        onClick={() => { setEditingNotes(plan.id); setEditNotesText(plan.notes); }}
                                        className="text-xs text-yellow-700 hover:underline"
                                      >Edit</button>
                                    </div>
                                    <p className="text-sm text-yellow-900 whitespace-pre-wrap">{plan.notes}</p>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => { setEditingNotes(plan.id); setEditNotesText(""); }}
                                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                    data-testid="add-notes-btn"
                                  >
                                    <StickyNote className="w-4 h-4" /> Add notes
                                  </button>
                                )}
                              </div>
                            )}

                            {/* File viewer */}
                            <LessonFileViewer plan={plan} token={token} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            </div>
          </CardContent>
        </Card>

        {/* Lesson Plan Templates — below the upload area */}
        <Card ref={templateCardRef} className="border-0 shadow-lg rounded-2xl">
          <CardContent className="p-6">
            <LessonPlanTemplates
              viewingMonth={viewingMonth}
              staffFilterId={staffFilterId}
              onView={(tmpl) => {
                setViewingTemplate(tmpl);
                topCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              startEditRef={startEditRef}
              onPrint={handleLessonPlanPrint}
            />
          </CardContent>
        </Card>
      </div>
      </FeatureGate>
    </AppShell>
  );
}
